import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getBusinessNotifications, updateNotificationStatus, markAllNotificationsAsRead } from '../services/api';

export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async ({ page = 1, limit = 10, reset = false } = {}) => {
    const res = await getBusinessNotifications(page, limit);
    const data = res?.data?.notifications || [];
    return { 
      items: data, 
      page, 
      limit,
      reset // Pass reset flag to know if we should replace or append
    };
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId) => {
    await updateNotificationStatus(notificationId);
    return notificationId;
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async () => {
    await markAllNotificationsAsRead();
    return true;
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    page: 1,
    limit: 10,
    unreadCount: 0,
    loading: false,
    error: null,
  },
  reducers: {
    markAllRead(state) {
      state.items = state.items.map(item => ({ ...item, status: 'read' }));
      state.unreadCount = 0;
    },
    markAsReadLocal(state, action) {
      const notificationId = action.payload;
      const notification = state.items.find(item => item._id === notificationId);
      if (notification && notification.status === 'unread') {
        notification.status = 'read';
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    updateUnreadCount(state, action) {
      const newCount = action.payload;
      const localCount = state.items.filter(n => n.status === 'unread').length;
      
      // Only update if the new count is reasonable or if we have no local data
      if (state.items.length === 0 || Math.abs(newCount - localCount) <= 2) {
        state.unreadCount = newCount;
        console.log('NOTIFICATIONS: Updated unread count from external source:', newCount);
      } else {
        console.log('NOTIFICATIONS: Ignoring external count due to discrepancy. Local:', localCount, 'External:', newCount);
        // Keep the local count as it's more accurate
        state.unreadCount = localCount;
      }
    },
    addNewNotification(state, action) {
      const incoming = action.payload;
      // Prevent duplicates based on unique _id (if provided)
      const existingIndex = state.items.findIndex(item => item._id === incoming._id);

      if (existingIndex === -1) {
        // New notification – add to the beginning of the list
        state.items.unshift(incoming);
      } else {
        // Update existing notification with any new fields
        state.items[existingIndex] = { ...state.items[existingIndex], ...incoming };
      }

      // Determine if the incoming notification should be considered unread.
      // Treat undefined status or any value other than 'read' as unread.
      const isUnread = !incoming.status || incoming.status.toLowerCase() !== 'read';

      if (isUnread) {
        state.unreadCount += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        // If reset is true or it's page 1, replace items
        // Otherwise append new items to existing ones
        if (action.payload.reset || action.payload.page === 1) {
          state.items = action.payload.items;
        } else {
          state.items = [...state.items, ...action.payload.items];
        }
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        // Always calculate unread count from local items - this is the source of truth
        const calculatedUnreadCount = state.items.filter((n) => n.status === 'unread').length;
        state.unreadCount = calculatedUnreadCount;
        console.log('NOTIFICATIONS: Calculated unread count from local items:', calculatedUnreadCount);
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.items.find(item => item._id === notificationId);
        if (notification && notification.status === 'unread') {
          notification.status = 'read';
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items = state.items.map(item => ({ ...item, status: 'read' }));
        state.unreadCount = 0;
      });
  }
});

export const { markAllRead, markAsReadLocal, updateUnreadCount, addNewNotification } = notificationsSlice.actions;
export const selectNotifications = (state) => state.notifications.items || [];
export const selectNotificationsLoading = (state) => state.notifications.loading;
export const selectUnreadNotifications = (state) => state.notifications.unreadCount || 0;

export default notificationsSlice.reducer; 